import { BaseClient } from '../core/client';
import type { BaseClientConfig, RequestOptions } from '../core/config';
import type {
  TaxClientConfig,
  CustomerTaxProfileRecord,
  CustomerTaxProfileDocuments,
  ReRequestTaxFormInput,
  EnableWithholdingInput,
  EnableWithholdingResponse,
  TINCheckResult,
  TaxRules,
  UpdateTaxRulesInput,
  UpdateReminderConfigInput,
  TaxRuleVersion,
  ComplianceStats,
  ComplianceStatsFilters,
  GlobalTaxRule,
  TreatyCountry,
  TreatyCountryListResponse,
  RunRemindersResponse,
} from './types';

export class TaxClient extends BaseClient {
  constructor(config: TaxClientConfig) {
    const baseConfig: BaseClientConfig = {
      baseURL: config.baseURL,
      tenantId: config.tenantId,
      apiKey: config.apiKey,
      headers: config.headers,
      timeout: config.timeout,
      retries: 3,
      debug: config.debug,
      environment: config.environment,
    };
    super(baseConfig);
  }

  // ==========================================================================
  // P1 — Customer Tax Profile (core compliance flow)
  // ==========================================================================

  async getCustomerTaxProfileRecord(customerID: string): Promise<CustomerTaxProfileRecord> {
    return this.request<CustomerTaxProfileRecord>(
      `/api/v1/tax/customer-tax-profiles/${customerID}`
    );
  }

  async requestTaxForm(customerID: string): Promise<CustomerTaxProfileRecord> {
    return this.request<CustomerTaxProfileRecord>(
      `/api/v1/tax/customer-tax-profiles/${customerID}/request-form`,
      { method: 'POST' }
    );
  }

  async checkTINStatus(customerID: string): Promise<TINCheckResult> {
    return this.requestWithRetry<TINCheckResult>(
      `/api/v1/tax/customer-tax-profiles/${customerID}/check-tin`,
      { method: 'POST' }
    );
  }

  async reRequestTaxForm(
    customerID: string,
    input: ReRequestTaxFormInput
  ): Promise<CustomerTaxProfileRecord> {
    return this.request<CustomerTaxProfileRecord>(
      `/api/v1/tax/customer-tax-profiles/${customerID}/re-request-form`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  }

  async enableBackupWithholding(
    customerID: string,
    input: EnableWithholdingInput
  ): Promise<EnableWithholdingResponse> {
    return this.request<EnableWithholdingResponse>(
      `/api/v1/tax/customer-tax-profiles/${customerID}/enable-withholding`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  }

  async getCustomerDocuments(customerID: string): Promise<CustomerTaxProfileDocuments> {
    return this.request<CustomerTaxProfileDocuments>(
      `/api/v1/tax/customer-tax-profiles/${customerID}/documents`
    );
  }

  async downloadTaxForm(
    customerID: string,
    requestOptions?: RequestOptions
  ): Promise<ArrayBuffer> {
    return this.request<ArrayBuffer>(
      `/api/v1/tax/customer-tax-profiles/${customerID}/download-form`,
      { method: 'GET' },
      undefined,
      { ...requestOptions, responseType: 'arraybuffer' }
    );
  }

  async runReminders(): Promise<RunRemindersResponse> {
    return this.request<RunRemindersResponse>('/api/v1/tax/reminders/run', {
      method: 'POST',
    });
  }

  /**
   * Update only the reminder configuration (frequency + max count) without
   * touching any other tax rule settings. Fetches current rules first and
   * merges the reminder fields before sending the update.
   *
   * Requires the caller to be authenticated as a Tenant Super Admin.
   * Throws VesantError with status 403 if the role requirement is not met.
   */
  async updateReminderConfig(input: UpdateReminderConfigInput): Promise<TaxRuleVersion> {
    const current = await this.getTaxRules();
    return this.updateTaxRules({
      trigger_account_creation: current.trigger_account_creation,
      trigger_first_withdrawal: current.trigger_first_withdrawal,
      trigger_threshold: current.trigger_threshold,
      trigger_manual: current.trigger_manual,
      trigger_tin_invalid: current.trigger_tin_invalid,
      trigger_w8ben_expiry: current.trigger_w8ben_expiry,
      trigger_tin_expired: current.trigger_tin_expired,
      us_withholding_enabled: current.us_withholding_enabled,
      non_us_withholding_enabled: current.non_us_withholding_enabled,
      transaction_action: current.transaction_action,
      w8ben_expiry_warning_days: current.w8ben_expiry_warning_days,
      tin_verification_due_date: current.tin_verification_due_date,
      email_sending_method: current.email_sending_method,
      display_tin_links_on_platform: current.display_tin_links_on_platform,
      tax_treaty_support: current.tax_treaty_support,
      reminder_frequency_days: input.reminder_frequency_days,
      reminder_max_count: input.reminder_max_count,
    });
  }

  // ==========================================================================
  // P2 — Tenant Tax Rules
  // ==========================================================================

  async getTaxRules(): Promise<TaxRules> {
    const res = await this.request<{ rules: TaxRules }>('/api/v1/tax/rules');
    return res.rules;
  }

  async updateTaxRules(input: UpdateTaxRulesInput): Promise<TaxRuleVersion> {
    const res = await this.request<{ version: TaxRuleVersion }>('/api/v1/tax/rules', {
      method: 'PUT',
      body: JSON.stringify({ rules: input }),
    });
    return res.version;
  }

  async getComplianceStats(filters?: ComplianceStatsFilters): Promise<ComplianceStats> {
    const params: Record<string, unknown> = {};
    if (filters?.time_range) params.time_range = filters.time_range;
    if (filters?.from_date) params.from_date = filters.from_date;
    if (filters?.to_date) params.to_date = filters.to_date;
    return this.request<ComplianceStats>(
      `/api/v1/tax/tenant/w-form-tenant-stats${this.buildQueryString(params)}`
    );
  }

  // ==========================================================================
  // P3 — Global Tax Rule & Treaty Countries
  // ==========================================================================

  async getCurrentGlobalTaxRule(): Promise<GlobalTaxRule> {
    const res = await this.request<{ success: boolean; message: string; data: GlobalTaxRule }>(
      '/api/v1/tm/tenant/global-tax-rules/current'
    );
    return res.data;
  }

  async listTreatyCountries(): Promise<TreatyCountryListResponse> {
    return this.request<TreatyCountryListResponse>('/api/v1/tax/treaty-countries');
  }

  async getTreatyCountry(code: string): Promise<TreatyCountry> {
    const res = await this.request<{ treaty_country: TreatyCountry }>(
      `/api/v1/tax/treaty-countries/${encodeURIComponent(code)}`
    );
    return res.treaty_country;
  }
}
